/**
 * Collection Contract Service
 * 
 * Handles interactions with UnitCollectionFactory and UnitCollection contracts.
 * - Deploy new collections via factory
 * - Mint NFTs to custom collections
 */

import { Contract, BrowserProvider, keccak256, toUtf8Bytes } from "ethers";
import { nftConfig, getCollectionFactoryAddress } from "@/config/nft";

// Factory ABI - for creating new collections
const factoryAbi = [
  "function createCollection(string name, string symbol, uint96 royaltyBps, string contractURI) external returns (address collection)",
  "function createCollectionSimple(string name, string symbol) external returns (address collection)",
  "function treasury() view returns (address)",
  "function mintFeeWei() view returns (uint256)",
  "function defaultRoyaltyBps() view returns (uint96)",
  "function getCreatorCollections(address creator) view returns (address[])",
  "function isUnitCollection(address collection) view returns (bool)",
  "event CollectionCreated(address indexed creator, address indexed collection, string name, string symbol, uint96 royaltyBps)",
];

// Collection ABI - for minting to user's collection
const collectionAbi = [
  "function mint(address to, string tokenURI_) external payable returns (uint256)",
  "function mintBatch(address to, string[] tokenURIs) external payable returns (uint256[])",
  "function burn(uint256 tokenId) external",
  "function creator() view returns (address)",
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function mintFeeWei() view returns (uint256)",
  "function royaltyBps() view returns (uint96)",
  "function nextTokenId() view returns (uint256)",
  "function totalMinted() view returns (uint256)",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "event Minted(address indexed creator, address indexed recipient, uint256 indexed tokenId, string tokenURI_, uint256 feePaid)",
  "event Burned(address indexed owner, uint256 indexed tokenId)",
];

const COLLECTION_CREATED_TOPIC = keccak256(
  toUtf8Bytes("CollectionCreated(address,address,string,string,uint96)")
);

const MINTED_EVENT_TOPIC = keccak256(
  toUtf8Bytes("Minted(address,address,uint256,string,uint256)")
);

/**
 * Generate a symbol from collection name
 * @param {string} name Collection name
 * @returns {string} Symbol (max 8 chars, uppercase)
 */
export function generateSymbol(name) {
  if (!name) return "UNIT";
  // Take first letters of words, or first 8 chars
  const words = name.trim().split(/\s+/);
  if (words.length > 1) {
    return words
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .substring(0, 8);
  }
  return name.toUpperCase().replace(/[^A-Z0-9]/g, "").substring(0, 8) || "UNIT";
}

/**
 * Deploy a new collection via the factory
 * @param {Object} params
 * @param {Object} params.signer Ethers signer
 * @param {string} params.chainId Chain ID in hex
 * @param {string} params.name Collection name
 * @param {string} [params.symbol] Collection symbol (auto-generated if not provided)
 * @param {number} [params.royaltyBps] Royalty in basis points (default: 500 = 5%)
 * @param {string} [params.contractURI] Collection metadata URI
 * @returns {Promise<Object>} { contractAddress, txHash, name, symbol, royaltyBps }
 */
export async function deployCollection({
  signer,
  chainId,
  name,
  symbol,
  royaltyBps = 500,
  contractURI = "",
}) {
  const factoryAddress = getCollectionFactoryAddress(chainId);
  if (!factoryAddress) {
    throw new Error(
      `Collection factory not deployed on chain ${chainId}. Use the shared Unit Marketplace instead.`
    );
  }

  const factory = new Contract(factoryAddress, factoryAbi, signer);
  const finalSymbol = symbol || generateSymbol(name);

  console.log("[CollectionContract] Deploying collection:", {
    name,
    symbol: finalSymbol,
    royaltyBps,
    contractURI,
  });

  // Estimate gas for user feedback
  const estimatedGas = await factory.createCollection.estimateGas(
    name,
    finalSymbol,
    royaltyBps,
    contractURI
  );
  console.log("[CollectionContract] Estimated gas:", estimatedGas.toString());

  // Deploy the collection
  const tx = await factory.createCollection(
    name,
    finalSymbol,
    royaltyBps,
    contractURI
  );
  console.log("[CollectionContract] Deploy tx sent:", tx.hash);

  const receipt = await tx.wait();
  console.log("[CollectionContract] Deploy confirmed, gas used:", receipt.gasUsed.toString());

  // Extract collection address from event
  let contractAddress = null;
  for (const log of receipt.logs || []) {
    try {
      const parsed = factory.interface.parseLog(log);
      if (parsed && parsed.name === "CollectionCreated") {
        contractAddress = parsed.args.collection;
        break;
      }
    } catch (e) {
      // Ignore logs from other contracts
    }
  }

  if (!contractAddress) {
    throw new Error("Failed to extract collection address from deployment transaction");
  }

  return {
    contractAddress,
    txHash: tx.hash,
    name,
    symbol: finalSymbol,
    royaltyBps,
  };
}

/**
 * Mint an NFT to a custom collection
 * @param {Object} params
 * @param {Object} params.signer Ethers signer
 * @param {string} params.collectionAddress Collection contract address
 * @param {string} params.recipient Recipient wallet address
 * @param {string} params.tokenURI IPFS metadata URI
 * @returns {Promise<Object>} { tokenId, txHash, mintFeeWei }
 */
export async function mintToCollection({
  signer,
  collectionAddress,
  recipient,
  tokenURI,
}) {
  console.log("[CollectionContract] mintToCollection called with:", {
    collectionAddress,
    recipient,
    tokenURI: tokenURI?.substring(0, 50) + "...",
    signerAddress: await signer.getAddress(),
    signerNetwork: await signer.provider?.getNetwork?.(),
  });

  const collection = new Contract(collectionAddress, collectionAbi, signer);

  // Verify the contract has code before calling - with retry for newly deployed contracts
  let code = await signer.provider.getCode(collectionAddress);
  console.log("[CollectionContract] Contract code length (first check):", code?.length || 0);
  
  // If no code, wait and retry - newly deployed contracts may not be visible immediately
  if (!code || code === "0x") {
    console.log("[CollectionContract] No code found, waiting 3s for node sync...");
    await new Promise(resolve => setTimeout(resolve, 3000));
    code = await signer.provider.getCode(collectionAddress);
    console.log("[CollectionContract] Contract code length (retry 1):", code?.length || 0);
    
    if (!code || code === "0x") {
      console.log("[CollectionContract] Still no code, waiting another 3s...");
      await new Promise(resolve => setTimeout(resolve, 3000));
      code = await signer.provider.getCode(collectionAddress);
      console.log("[CollectionContract] Contract code length (retry 2):", code?.length || 0);
      
      if (!code || code === "0x") {
        throw new Error(`No contract code at ${collectionAddress}. The collection may not have been deployed yet. Please wait a moment and try again.`);
      }
    }
  }

  // Get mint fee from contract - with retry logic for newly deployed contracts
  let mintFeeWei;
  try {
    mintFeeWei = await collection.mintFeeWei();
    console.log("[CollectionContract] Mint fee (first attempt):", mintFeeWei.toString(), "wei");
  } catch (viewError) {
    console.warn("[CollectionContract] First mintFeeWei call failed, retrying after short delay:", viewError.message);
    // Wait 2 seconds for node state propagation
    await new Promise(resolve => setTimeout(resolve, 2000));
    try {
      mintFeeWei = await collection.mintFeeWei();
      console.log("[CollectionContract] Mint fee (retry):", mintFeeWei.toString(), "wei");
    } catch (retryError) {
      console.error("[CollectionContract] Retry also failed:", retryError);
      // Try a direct eth_call as last resort
      const mintFeeFunctionSelector = "0x06220534"; // keccak256("mintFeeWei()").slice(0,10)
      const rawResult = await signer.provider.call({
        to: collectionAddress,
        data: mintFeeFunctionSelector,
      });
      console.log("[CollectionContract] Raw eth_call result:", rawResult);
      if (!rawResult || rawResult === "0x") {
        throw new Error(`Contract at ${collectionAddress} does not respond to mintFeeWei() calls. It may not be a valid UnitCollection contract.`);
      }
      mintFeeWei = BigInt(rawResult);
      console.log("[CollectionContract] Mint fee (from raw call):", mintFeeWei.toString(), "wei");
    }
  }

  // Mint the NFT
  const tx = await collection.mint(recipient, tokenURI, {
    value: mintFeeWei,
  });
  console.log("[CollectionContract] Mint tx sent:", tx.hash);

  const receipt = await tx.wait();
  console.log("[CollectionContract] Mint confirmed, gas used:", receipt.gasUsed.toString());

  // Extract tokenId from event
  let tokenId = null;
  for (const log of receipt.logs || []) {
    try {
      const parsed = collection.interface.parseLog(log);
      if (parsed && parsed.name === "Minted") {
        tokenId = parsed.args.tokenId?.toString();
        break;
      }
    } catch (e) {
      // Ignore logs from other contracts
    }
  }

  return {
    tokenId,
    txHash: tx.hash,
    mintFeeWei: mintFeeWei.toString(),
  };
}

/**
 * Batch mint multiple NFTs to a custom collection in one transaction
 * @param {Object} params
 * @param {Object} params.signer Ethers signer
 * @param {string} params.collectionAddress Collection contract address
 * @param {string} params.recipient Recipient wallet address
 * @param {string[]} params.tokenURIs Array of IPFS metadata URIs
 * @returns {Promise<Object>} { tokenIds, txHash, mintFeeWei, count }
 */
export async function mintBatchToCollection({
  signer,
  collectionAddress,
  recipient,
  tokenURIs,
}) {
  const count = tokenURIs.length;
  console.log("[CollectionContract] mintBatchToCollection called with:", {
    collectionAddress,
    recipient,
    count,
    signerAddress: await signer.getAddress(),
  });

  if (!tokenURIs || count === 0) {
    throw new Error("No tokenURIs provided for batch mint");
  }

  const collection = new Contract(collectionAddress, collectionAbi, signer);

  // Verify the contract has code - with retry for newly deployed contracts
  let code = await signer.provider.getCode(collectionAddress);
  if (!code || code === "0x") {
    console.log("[CollectionContract] No code found, waiting 3s for node sync...");
    await new Promise(resolve => setTimeout(resolve, 3000));
    code = await signer.provider.getCode(collectionAddress);
    if (!code || code === "0x") {
      throw new Error(`No contract code at ${collectionAddress}. The collection may not have been deployed yet.`);
    }
  }

  // Get mint fee from contract
  let mintFeeWei;
  try {
    mintFeeWei = await collection.mintFeeWei();
    console.log("[CollectionContract] Mint fee per NFT:", mintFeeWei.toString(), "wei");
  } catch (viewError) {
    console.warn("[CollectionContract] mintFeeWei call failed, retrying:", viewError.message);
    await new Promise(resolve => setTimeout(resolve, 2000));
    mintFeeWei = await collection.mintFeeWei();
  }

  // Calculate total fee for batch
  const totalFee = mintFeeWei * BigInt(count);
  console.log("[CollectionContract] Total batch fee:", totalFee.toString(), "wei for", count, "NFTs");

  // Estimate gas for the batch mint
  const estimatedGas = await collection.mintBatch.estimateGas(recipient, tokenURIs, {
    value: totalFee,
  });
  console.log("[CollectionContract] Batch mint estimated gas:", estimatedGas.toString());

  // Execute batch mint
  const tx = await collection.mintBatch(recipient, tokenURIs, {
    value: totalFee,
  });
  console.log("[CollectionContract] Batch mint tx sent:", tx.hash);

  const receipt = await tx.wait();
  console.log("[CollectionContract] Batch mint confirmed, gas used:", receipt.gasUsed.toString());

  // Extract tokenIds from events
  const tokenIds = [];
  for (const log of receipt.logs || []) {
    try {
      const parsed = collection.interface.parseLog(log);
      if (parsed && parsed.name === "Minted") {
        tokenIds.push(parsed.args.tokenId?.toString());
      }
    } catch (e) {
      // Ignore logs from other contracts
    }
  }

  console.log("[CollectionContract] Batch minted tokenIds:", tokenIds);

  return {
    tokenIds,
    txHash: tx.hash,
    mintFeeWei: mintFeeWei.toString(),
    totalFeeWei: totalFee.toString(),
    count,
  };
}

/**
 * Burn (destroy) an NFT from a collection
 * Only the token owner or approved operator can burn
 * @param {Object} params
 * @param {Object} params.signer Ethers signer
 * @param {string} params.contractAddress Collection or marketplace contract address
 * @param {string|number} params.tokenId Token ID to burn
 * @returns {Promise<Object>} { txHash, tokenId }
 */
export async function burnNFT({ signer, contractAddress, tokenId }) {
  console.log("[CollectionContract] burnNFT called with:", {
    contractAddress,
    tokenId,
    signerAddress: await signer.getAddress(),
  });

  const collection = new Contract(contractAddress, collectionAbi, signer);

  // Verify the contract has code
  const code = await signer.provider.getCode(contractAddress);
  if (!code || code === "0x") {
    throw new Error(`No contract code at ${contractAddress}`);
  }

  // Verify caller owns the token
  const signerAddress = await signer.getAddress();
  const owner = await collection.ownerOf(tokenId);
  if (owner.toLowerCase() !== signerAddress.toLowerCase()) {
    throw new Error(`You do not own this NFT. Current owner: ${owner}`);
  }

  // Estimate gas first for user feedback
  const estimatedGas = await collection.burn.estimateGas(tokenId);
  console.log("[CollectionContract] Burn estimated gas:", estimatedGas.toString());

  // Execute burn
  const tx = await collection.burn(tokenId);
  console.log("[CollectionContract] Burn tx sent:", tx.hash);

  const receipt = await tx.wait();
  console.log("[CollectionContract] Burn confirmed, gas used:", receipt.gasUsed.toString());

  return {
    txHash: tx.hash,
    tokenId: tokenId.toString(),
  };
}

/**
 * Get collection info
 * @param {Object} provider Ethers provider
 * @param {string} collectionAddress Collection contract address
 * @returns {Promise<Object>} Collection info
 */
export async function getCollectionInfo(provider, collectionAddress) {
  const collection = new Contract(collectionAddress, collectionAbi, provider);
  
  const [name, symbol, creator, mintFeeWei, royaltyBps, totalMinted] = await Promise.all([
    collection.name(),
    collection.symbol(),
    collection.creator(),
    collection.mintFeeWei(),
    collection.royaltyBps(),
    collection.totalMinted(),
  ]);

  return {
    name,
    symbol,
    creator,
    mintFeeWei: mintFeeWei.toString(),
    royaltyBps: Number(royaltyBps),
    totalMinted: Number(totalMinted),
  };
}

/**
 * Check if an address is a UnitCollection (deployed by factory)
 * @param {Object} provider Ethers provider
 * @param {string} chainId Chain ID in hex
 * @param {string} collectionAddress Address to check
 * @returns {Promise<boolean>}
 */
export async function isUnitCollection(provider, chainId, collectionAddress) {
  const factoryAddress = getCollectionFactoryAddress(chainId);
  if (!factoryAddress) return false;

  const factory = new Contract(factoryAddress, factoryAbi, provider);
  return factory.isUnitCollection(collectionAddress);
}

/**
 * Get all collections created by a wallet address
 * @param {Object} provider Ethers provider
 * @param {string} chainId Chain ID in hex
 * @param {string} creatorAddress Creator's wallet address
 * @returns {Promise<string[]>} Array of collection addresses
 */
export async function getCreatorCollectionsOnChain(provider, chainId, creatorAddress) {
  const factoryAddress = getCollectionFactoryAddress(chainId);
  if (!factoryAddress) return [];

  const factory = new Contract(factoryAddress, factoryAbi, provider);
  return factory.getCreatorCollections(creatorAddress);
}
